import React from 'react';
import { ChakraProvider, theme, Center, Spinner } from '@chakra-ui/react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import Estimator from './components/Estimator';
import History from './components/History';
import Pricing from './components/Pricing';
import NavBar from './components/NavBar';
import Labor from './components/Labor';
import Settings from './components/Settings';
import LoginPage from './components/Login';
import SignUp from './components/SignUp';
import PasswordReset from './components/PasswordReset';
import SignEstimate from './components/SignEstimate';
import SmallDeviceMessage from './components/SmallDeviceMessage';
import useUser from './hooks/useUser';
import { useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import { useMediaQuery } from '@chakra-ui/react';

// The customer-facing e-signature page is public (no account required) and
// must never show the authenticated app's nav/header, regardless of whether
// the person viewing it happens to be logged in elsewhere in this browser.
function AppShell({ user, isLargerThan800 }) {
  const location = useLocation();
  const isPublicSigningPage = location.pathname.startsWith('/sign/');

  return (
    <>
      {!isPublicSigningPage && user && isLargerThan800 && <NavBar />}
      <Routes>
        <Route path="/sign/:token" element={<SignEstimate />} />
        {user && user?.profile !== 'salesperson' ? (
          <>
            <Route path="/estimator" element={<Estimator />} />
            <Route path="/history" element={<History />} />
            {/* Material (Pricing) and Labor are desktop-only tools -- on
                mobile, direct navigation to these routes falls back to the
                Estimator instead, matching the nav bar already hiding them. */}
            <Route
              path="/prices"
              element={isLargerThan800 ? <Pricing /> : <Estimator />}
            />
            <Route
              path="/labor"
              element={isLargerThan800 ? <Labor /> : <Estimator />}
            />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Estimator />} />
          </>
        ) : user ? (
          <>
            <Route path="/estimator" element={<Estimator />} />
            <Route path="/history" element={<History />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Estimator />} />
          </>
        ) : (
          <>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/reset" element={<PasswordReset />} />
            <Route path="*" element={<LoginPage />} />
          </>
        )}
      </Routes>
    </>
  );
}

function App() {
  const { user, isUserLoading } = useUser();
  const [isLargerThan800] = useMediaQuery('(min-width: 800px)');

  useEffect(() => {
    if (user && process.env.REACT_APP_ENVIRONMENT === 'prod') {
      mixpanel.identify(user.email);
    }
  }, [user]);

  return (
    <ChakraProvider theme={theme}>
      {isUserLoading ? (
        <Center height="100vh">
          <Spinner size="xl" color="blue.500" />
        </Center>
      ) : (
        <Router>
          <AppShell user={user} isLargerThan800={isLargerThan800} />
        </Router>
      )}
    </ChakraProvider>
  );
}

export default App;
